/**
 * Retainer en propositie per regel (Joris 1 okt 2026), rustig in één cel: twee kleine regels tekst.
 *
 * Grijs = afgeleid uit de opdracht-mix (retainer: ≥ drempel doorlopend werk in 12 maanden;
 * propositie: doel-mix van 24 maanden), donker = handmatig gekozen. Klik opent een klein menu;
 * "Afleiden" zet de handmatige keuze terug. Elke keuze is ongedaan te maken (toast + Ctrl+Z);
 * versiecontrole voorkomt stil overschrijven.
 */
import React, { useState } from 'react';
import { Box, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import toast from 'react-hot-toast';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { budgetApi } from './budgetApi';
import { PROPOSITIE_LABEL, mixTekst, propositieWeergave, retainerWeergave } from './budgetLogica';

const KLEUR = { handmatig: '#334155', afgeleid: '#94a3b8', onbekend: '#cbd5e1' };

function Knop({ weergave, label, onClick, magWijzigen }) {
  const stijl = {
    display: 'block', p: 0, border: 0, bgcolor: 'transparent', font: 'inherit', fontSize: 12.5, lineHeight: 1.5,
    textAlign: 'left', whiteSpace: 'nowrap', color: KLEUR[weergave.bron] || KLEUR.afgeleid,
    fontWeight: weergave.bron === 'handmatig' ? 600 : 400,
  };
  return (
    <Tooltip title={weergave.titel} placement="left">
      {magWijzigen ? (
        <Box component="button" type="button" onClick={onClick} aria-label={label}
          sx={{ ...stijl, cursor: 'pointer', '&:hover': { color: '#4f46e5' } }}>{weergave.tekst}</Box>
      ) : <Box component="span" sx={stijl}>{weergave.tekst}</Box>}
    </Tooltip>
  );
}

export default function TypeCel({ regel, jaar, magWijzigen, proposities, onOpgeslagen }) {
  const [menu, setMenu] = useState(null);   // { soort: 'retainer'|'propositie', anker }
  const [bezig, setBezig] = useState(false);
  if (regel.soort === 'wees') return null;

  const handRetainer = regel.retainer_bron === 'handmatig' ? regel.retainer : null;
  const ret = retainerWeergave(regel);
  const prop = propositieWeergave(regel);
  const afgeleidRetainer = regel.retainer_afgeleid == null ? 'onbekend' : (regel.retainer_afgeleid ? 'retainer' : 'geen retainer');
  const opties = proposities?.length ? proposities : Object.keys(PROPOSITIE_LABEL).filter((k) => k !== 'onbekend')
    .map((k) => ({ sleutel: k, label: PROPOSITIE_LABEL[k] }));

  const bewaar = async (veld, waarde, tekst) => {
    setMenu(null);
    const huidig = veld === 'retainer' ? handRetainer : (regel.propositie ?? null);
    if (huidig === waarde) return;
    setBezig(true);
    try {
      const uit = await budgetApi.bewaar(regel.sleutel, { [veld]: waarde, versie: regel.versie }, jaar);
      await onOpgeslagen();
      biedOngedaanAan(`${regel.naam}: ${tekst}`, async () => {
        await budgetApi.bewaar(regel.sleutel, { ...uit.herstel, versie: uit.versie }, jaar);
        await onOpgeslagen();
      }, { eigenKnop: true });
    } catch (e) {
      if (e.status === 409) {
        toast.error(`${regel.naam} is intussen door iemand anders gewijzigd — kies opnieuw`);
        await onOpgeslagen();
      } else toast.error(e.message);
    } finally { setBezig(false); }
  };

  const open = (soort) => (e) => { e.stopPropagation(); if (!bezig) setMenu({ soort, anker: e.currentTarget }); };

  return (
    <Box onClick={(e) => e.stopPropagation()} sx={{ opacity: bezig ? 0.5 : 1 }}>
      <Knop weergave={ret} label={`Retainer ${regel.naam}`} onClick={open('retainer')} magWijzigen={magWijzigen} />
      <Knop weergave={prop} label={`Propositie ${regel.naam}`} onClick={open('propositie')} magWijzigen={magWijzigen} />
      <Menu anchorEl={menu?.anker} open={!!menu} onClose={() => setMenu(null)}
        MenuListProps={{ dense: true }} onClick={(e) => e.stopPropagation()}>
        {menu?.soort === 'retainer' && [
          <MenuItem key="ja" selected={handRetainer === true} onClick={() => bewaar('retainer', true, 'retainer')}>
            <ListItemText primary="Retainer" secondary="Doorlopende opdracht" />
          </MenuItem>,
          <MenuItem key="nee" selected={handRetainer === false} onClick={() => bewaar('retainer', false, 'geen retainer')}>
            <ListItemText primary="Geen retainer" secondary="Losse opdrachten" />
          </MenuItem>,
          <MenuItem key="af" selected={handRetainer === null} onClick={() => bewaar('retainer', null, 'retainer weer afgeleid')}>
            <ListItemText primary="Afleiden uit de opdracht-mix" secondary={`Nu: ${afgeleidRetainer}`} />
          </MenuItem>,
        ]}
        {menu?.soort === 'propositie' && [
          <MenuItem key="af" selected={!regel.propositie} onClick={() => bewaar('propositie', null, 'propositie weer afgeleid')}>
            <ListItemText primary="Afleiden uit de doel-mix"
              secondary={Object.keys(regel.propositie_mix || {}).length ? `Nu: ${mixTekst(regel.propositie_mix)}` : 'Nu: onbekend (geen historie)'} />
          </MenuItem>,
          ...opties.map((p) => (
            <MenuItem key={p.sleutel} selected={regel.propositie === p.sleutel}
              onClick={() => bewaar('propositie', p.sleutel, `propositie ${p.label}`)}>
              <ListItemText primary={p.label} secondary={p.omschrijving} />
            </MenuItem>
          )),
        ]}
      </Menu>
    </Box>
  );
}
